// @flow
import { has } from "../../utils/helpers/objects";

import BaseCollection from "../base/BaseCollection";
import SortOptionModel from "../sorting/SortOptionModel";
import ListHref from "../href/ListHref";

import type GroupingModel from "../grouping/GroupingModel";
import IllegalArgumentException from "../../exceptions/IllegalArgumentException";

/**
 */
class SortingModel extends BaseCollection<SortOptionModel> {
  /**
   */
  constructor(
    contributions: Object,
    labels: { [key: string]: string },
    grouping: GroupingModel,
    currentSort: string,
  ) {
    super();

    if (has(contributions?.sorting, "attributes")) {
      this.collection = contributions.sorting.attributes.map((attributeKey) => {
        const label = labels[attributeKey];
        const group = grouping && grouping.getGroupByAttributeKey(attributeKey);

        return new SortOptionModel(attributeKey, label, group);
      });

      this.setSelected(currentSort);
    }
  }

  /**
   */
  get name(): string {
    return "sort";
  }

  /**
   */
  get value(): string {
    return this.filter((option) => option.selected)
      .map((option) => option.value)
      .join(",");
  }

  /**
   */
  set value(value: string) {
    this.reset();
    this.setSelected(value);
  }

  /**
   */
  get param(): string {
    return this.value;
  }

  /**
   */
  get options(): Array<SortOptionModel> {
    return this.collection;
  }

  /**
   */
  reset() {
    this.collection.forEach((option) => option.reset());
  }
  /**
   */
  setSelected(value: string = "") {
    value.split(",").forEach((sortItem) => {
      const [key, direction] = sortItem.split(" ");

      this.collection.forEach((option) => {
        if (option.key === key) {
          option.selected = true;
          if (direction === "asc") {
            option.sortorder = "asc";
          } else if (direction === "desc") {
            option.sortorder = "desc";
          }
        }
      });
    });
  }

  /**
   * Retrieve a sort option from the model that can be used in the querystring to set it in the opposite direction
   * For example a sort option in the model with key books and direction descending will be retrieved as 'Books asc'
   */
  getUpdateQuerystring(optionKey: string): string {
    if (!optionKey) {
      throw new IllegalArgumentException(
        "Missing option key as input argument",
      );
    }

    return this.filter((option) => option.key === optionKey)
      .map((option) =>
        option.key === optionKey ? option.oppositeValue : option.value,
      )
      .join(",");
  }

  /**
   * @see {@link SortingModel#getUpdateQuerystring}
   */
  getUpdateQuerystringBySortOption(sortOption: SortOptionModel): string {
    if (!(sortOption instanceof SortOptionModel)) {
      throw new IllegalArgumentException(
        "Missing SortOptionModel as input argument",
      );
    }

    return this.getUpdateQuerystring(sortOption.key);
  }

  /**
   */
  createListHref(listHref: ListHref, sortOption: SortOptionModel): ListHref {
    const listhref = new ListHref(listHref);
    const hasPage = listhref.prefix
      ? listhref.hasParameter("page", listhref.prefix)
      : listhref.hasParameter("page");
    if (hasPage) {
      listhref.page = 1;
    }
    listhref.sort = this.getUpdateQuerystringBySortOption(sortOption);

    return listhref;
  }
}

export default SortingModel;
